import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { COMPANY, GOOGLE_RATING, GOOGLE_REVIEWS } from "@/data/content";

function Stars({ rating = 5, className = "h-4 w-4" }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} von 5 Sternen`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={className}
          fill={i <= Math.round(rating) ? "#FBBC04" : "none"}
          stroke={i <= Math.round(rating) ? "#FBBC04" : "#C7CDD4"}
        />
      ))}
    </span>
  );
}

function GoogleG({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17Z"/>
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7A21.99 21.99 0 0 0 24 46Z"/>
      <path fill="#FBBC05" d="M11.69 28.18A13.2 13.2 0 0 1 11 24c0-1.45.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7Z"/>
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.95 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07Z"/>
    </svg>
  );
}

export default function Reviews() {
  const hasReviews = Array.isArray(GOOGLE_REVIEWS) && GOOGLE_REVIEWS.length > 0;
  const ratingValue = GOOGLE_RATING?.value;
  const ratingCount = GOOGLE_RATING?.count;

  return (
    <section id="bewertungen" className="section-pad bg-white" data-testid="section-bewertungen">
      <div className="container-x">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm font-bold tracking-wide text-[color:var(--brand-green-alt)] uppercase">Bewertungen</span>
          <h2 className="mt-2 font-display text-4xl sm:text-5xl text-[color:var(--brand-navy)]">Das sagen unsere Kunden</h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground">Echte Rückmeldungen von Menschen, die auf unseren Hausnotruf vertrauen.</p>
        </div>

        {/* Google rating summary card */}
        <div className="mt-10 flex justify-center">
          <div className="flex flex-col sm:flex-row items-center gap-5 rounded-2xl border border-border bg-secondary px-7 py-5 card-shadow" data-testid="google-rating-card">
            <div className="flex items-center gap-3">
              <GoogleG className="h-9 w-9" />
              <span className="text-lg font-bold text-[color:var(--brand-navy)]">Google Bewertungen</span>
            </div>
            <div className="hidden sm:block h-10 w-px bg-border" />
            <div className="flex items-center gap-3">
              {ratingValue ? (
                <>
                  <span className="font-display text-4xl text-[color:var(--brand-navy)]">{ratingValue.toFixed(1)}</span>
                  <div>
                    <Stars rating={ratingValue} className="h-5 w-5" />
                    {ratingCount ? <p className="text-sm text-muted-foreground mt-0.5">{ratingCount.toLocaleString("de-DE")} Rezensionen</p> : null}
                  </div>
                </>
              ) : (
                <Stars rating={5} className="h-6 w-6" />
              )}
            </div>
            <a
              href={COMPANY.googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-2 rounded-xl bg-[color:var(--brand-navy)] text-white px-5 py-3 font-bold hover:bg-[color:var(--brand-navy-alt)] transition-colors"
            >
              Auf Google ansehen <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Individual reviews */}
        {hasReviews && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="reviews-grid">
            {GOOGLE_REVIEWS.map((r, i) => (
              <motion.blockquote
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                className="rounded-2xl border border-border bg-white p-6 card-shadow card-shadow-hover"
                data-testid="review-card"
              >
                <div className="flex items-center justify-between">
                  <Stars rating={r.rating || 5} />
                  <GoogleG className="h-5 w-5" />
                </div>
                <p className="mt-4 text-[15px] leading-relaxed text-[color:var(--brand-navy)]">“{r.text}”</p>
                <footer className="mt-5 flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[color:var(--brand-green)] text-white font-bold">
                    {r.initial || (r.author ? r.author.charAt(0) : "K")}
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-[color:var(--brand-navy)]">{r.author}</span>
                    {r.date && <span className="block text-xs text-muted-foreground">{r.date}</span>}
                  </span>
                </footer>
              </motion.blockquote>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
